/* eslint-disable no-param-reassign */
import moment from 'moment-timezone';
import { getLocale } from 'umi';
import Regexp from '../../../infrastructure/utilities/regexp';
import Fn from './comparison';

/*
 * AddDays(date, days)
 *
 * This function add the specified number of days to the specified date parameter.
 *
 * SAMPLES
 * @my_date = 2019-12-31
 * AddDays(@my_date, 3) -> 2020-01-03
 *
 * INPUT
 * date: The date you want to use.Must be a date object  or must supply the parameter parseFormat.
 * days: The number of days to add.
 * parseFormat: the date parameter format
 * returnFormat: date parameter return format
 *
 * OUTPUT
 * Returns the result date of adding the specified days to the source date param.
 */
function AddDays(date, days, parseFormat, returnFormat) {
  if (date == null || date.length === 0) return '';
  if (parseFormat == null || parseFormat.length === 0) parseFormat = null;
  if (returnFormat == null || returnFormat.length === 0) {
    returnFormat = parseFormat === null ? _TryGuessDateFormat(date) : parseFormat;
  }

  const parsedDate = date instanceof Date ? date : _ParseDate(date, parseFormat);

  return moment(new Date(parsedDate.setDate(parsedDate.getDate() + +days))).format(returnFormat);
}

/*
 * AddMonths(date, months, parseFormat, returnFormat)
 *
 * This function add the specified number of months to the specified date parameter.
 *
 * SAMPLES
 * @my_date = 2019-12-31
 * AddMonths(@my_date, 4) -> 2020-04-30
 *
 * INPUT
 * date: The date you want to use.Must be a date object or must supply the parameter parseFormat.
 * months: The number of months to add
 * parseFormat: the date parameter format
 * returnFormat: date parameter return format
 *
 * OUTPUT
 * Returns the result date of adding the specified months to the source date param.
 */
function AddMonths(date, months, parseFormat, returnFormat) {
  if (date === null || date === undefined || date.length === 0) {
    return '';
  }
  if (parseFormat === null || parseFormat === undefined || parseFormat.length === 0) {
    parseFormat = null;
  }
  if (returnFormat === null || returnFormat === undefined || returnFormat.length === 0) {
    returnFormat = parseFormat === null ? _TryGuessDateFormat(date) : parseFormat;
  }
  let parsedDate = date instanceof Date ? date : _ParseDate(date, parseFormat);

  return moment(new Date(parsedDate.setMonth(parsedDate.getMonth() + months))).format(returnFormat);
}

/*
 * AddYears(date, years, parseFormat, returnFormat)
 *
 * This function add the specified number of years to the specified date parameter.
 *
 * SAMPLES
 * @my_date = 2019-12-31
 * AddYears(@my_date, 3) -> 2023-12-31
 *
 * INPUT
 * date: The date you want to use.Must be a date object  or must supply the parameter parseFormat.
 * years: The number of years to add
 * parseFormat: the date parameter format
 * returnFormat: date parameter return format
 *
 * OUTPUT
 * Returns the result date of adding the specified years to the source date param.
 */
function AddYears(date, years, parseFormat, returnFormat) {
  if (date === null || date === undefined || date.length === 0) {
    return '';
  }
  if (parseFormat === null || parseFormat === undefined || parseFormat.length === 0) {
    parseFormat = null;
  }
  if (returnFormat === null || returnFormat === undefined || returnFormat.length === 0) {
    returnFormat = parseFormat === null ? _TryGuessDateFormat(date) : parseFormat;
  }
  let parsedDate = date instanceof Date ? date : _ParseDate(date, parseFormat);

  return moment(new Date(parsedDate.setYear(parsedDate.getFullYear() + years))).format(
    returnFormat,
  );
}

/*
 * Now(timeZone, returnFormat)
 *
 * This functions returns the current datetime based on a given timeZone.
 *
 * SAMPLES
 * Now("America/Lima") -> 2022-07-19 15:01:14.829000-05:00
 *
 * INPUT
 * timeZone: Optional. It must be a specific timeZone.Check this link to see the supported values (TZ database name column).
 * returnFormat: date parameter return format
 *
 * OUTPUT
 * Returns the current date-time in text format.
 */
function Now(timeZone, returnFormat) {
  if (returnFormat == null || returnFormat.length === 0) {
    returnFormat = `${_CurrentLocaleDateFormat()} HH:mm:ss`;
  }
  return timeZone == null || timeZone.length === 0
    ? moment().format(_CleanMomentFormat(returnFormat))
    : moment().tz(timeZone).format(_CleanMomentFormat(returnFormat));
}

/*
 * DatePart(date, part, parseFormat)
 *
 * This functions returns the date part of given date
 *
 * SAMPLES
 * DatePart("2022-07-21","day") -> 21
 * DatePart("2022-07-21","month") -> 07
 * DatePart("2022-07-21","year") -> 2022
 * DatePart("07/21/2022","month","MM/dd/yyyy") -> 07
 * DatePart("21/07/2022","month","dd/MM/yyyy") -> 07
 *
 * INPUT
 * date: It must be a valid date
 * part: The date part you want to get.
 *     Allowed values
 *     - year = Year
 *     - quarter = Quarter
 *     - month = month
 *     - monthName = Month Name
 *     - dayOfYear = Day of the year
 *     - day = Day of the month
 *     - week = Week
 *     - isoWeek = ISO week
 *     - weekday = Weekday
 *     - weekdayName = Weekday Name
 *     - hour = hour
 *     - minute = Minute
 *     - second = Second
 * parseFormat: Optional. The supply date format. Default value is 'yyyy-MM-dd' for date and 'yyyy-MM-ddTHH:mm:ss' for datetime
 *
 * OUTPUT
 * Returns the current date part
 */
function DatePart(date, part, parseFormat) {
  if (date === null || date === undefined || date.length === 0) {
    return '';
  }

  if (parseFormat === null || parseFormat === undefined || parseFormat.length === 0) {
    parseFormat = null;
  }

  let parsedDate = date instanceof Date ? date : _ParseDate(date, parseFormat);

  switch (part) {
    case 'year':
      return parsedDate.getFullYear();

    case 'quarter':
      return Math.floor(parsedDate.getMonth() / 3 + 1);

    case 'month':
      return parsedDate.getMonth() + 1;

    case 'monthName':
      return moment(parsedDate).format('MMMM');

    case 'dayOfYear':
      return moment(parsedDate).dayOfYear();

    case 'day':
      return parsedDate.getDate();

    case 'week':
      return moment(parsedDate).week();

    case 'isoWeek':
      return moment(parsedDate).isoWeeks();

    case 'weekday':
      return moment(parsedDate).weekday();

    case 'weekdayName':
      return moment(parsedDate).format('dddd');

    case 'hour':
      return parsedDate.getHours();

    case 'minute':
      return parsedDate.getMinutes();

    case 'second':
      return parsedDate.getSeconds();

    default:
      return '';
  }
}

/*
 * DateDiff(startDate, endDate, parseFormat, unit)
 *
 * This function returns the count (as a signed integer value)
 * of the specified datepart boundaries crossed between the specified startDate and endDate
 *
 * SAMPLES
 * DateDiff("2022-07-21","2022-07-22",null,"day") -> 1
 *
 * INPUT
 * startDate: It must be a valid date
 * endDate: It must be a valid date
 * parseFormat: Optional. The supply date format. Default value is 'yyyy-MM-dd' for date and 'yyyy-MM-ddTHH:mm:ss' for datetime
 * unit: The date diff part you want to get.
 *     Allowed values
 *     - years = Years
 *     - months = Months
 *     - weeks = Weeks
 *     - days = Days
 *     - hours = hours
 *     - minutes = Minutes
 *     - seconds = Seconds
 *
 * OUTPUT
 * Returns the current date diff
 */
function DateDiff(startDate, endDate, unit = 'days', parseFormat = '') {
  if (parseFormat == null || parseFormat?.length === 0) parseFormat = null;
  if (unit == null || unit?.length === 0) unit = 'days';

  if (!(startDate && endDate)) return '';

  let parsedStartDate = startDate instanceof Date ? startDate : _ParseDate(startDate, parseFormat);
  let parsedEndDate = endDate instanceof Date ? endDate : _ParseDate(endDate, parseFormat);

  return moment(parsedStartDate).diff(moment(parsedEndDate), unit);
}

function ToDate(value, parseFormat, returnFormat) {
  if (parseFormat === null || parseFormat === undefined || parseFormat.length === 0) {
    parseFormat = moment.localeData(getLocale()).longDateFormat('L');
  }
  if (returnFormat === null || returnFormat === undefined || returnFormat.length === 0) {
    returnFormat = returnFormat !== null ? returnFormat : 'YYYY-MM-DD';
  }

  let newDate = moment(value, _PrepareMomentFormat(parseFormat));

  return newDate.format(_PrepareMomentFormat(returnFormat));
}

/*
 * AddBusinessDays(date, days, parseFormat, returnFormat, excludedDates)
 *
 * This function add the specified number of business days to the specified date parameter considering excludedDates
 *
 * SAMPLES
 * @my_date = 2019-12-31
 * AddBusinessDays(@my_date, 3, null, null, excludedDates) -> 2020-01-03
 *
 * INPUT
 * date: The date you want to use.Must be a date object  or must supply the parameter parseFormat.
 * days: The number of days to add.
 * parseFormat: the date parameter format
 * returnFormat: date parameter return format
 * excludedDates: array of excluded dates, in format MM-dd
 *
 * OUTPUT
 * Returns the result date of adding the specified days to the source date param.
 */
function AddBusinessDays(date, days, parseFormat, returnFormat, excludedDates) {
  if (date === null || date === undefined || date.length === 0) {
    return '';
  }
  if (parseFormat === null || parseFormat === undefined || parseFormat.length === 0) {
    parseFormat = null;
  }
  if (returnFormat === null || returnFormat === undefined || returnFormat.length === 0) {
    returnFormat = parseFormat === null ? _TryGuessDateFormat(date) : parseFormat;
  }
  let parsedDate = date instanceof Date ? date : _ParseDate(date, parseFormat);

  var date = new Date(parsedDate.setDate(parsedDate.getDate()));

  if (!(Array.isArray(excludedDates) && excludedDates.every((item) => typeof item === 'string'))) {
    excludedDates = [];
  }

  // Normalizar el array de fechas excluidas
  for (let i = 0; i < excludedDates.length; i++) {
    let [month, day] = excludedDates[i].split('-');
    excludedDates[i] = (month + '').padStart(2, '0') + '-' + (day + '').padStart(2, '0');
  }

  var endDate = date;
  let count = 0;

  var sum = true;
  if (days < 0) {
    sum = false;

    days = Math.abs(days);
  }

  while (count < days) {
    if (sum) {
      endDate = new Date(date.setDate(date.getDate() + 1));
    } else {
      endDate = new Date(date.setDate(date.getDate() - 1));
    }

    //Date.getDay() gives weekday starting from 0(Sunday) to 6(Saturday)
    if (endDate.getDay() !== 0 && endDate.getDay() !== 6) {
      // validate not exist in exluded dates
      if (excludedDates.length > 0) {
        var month = endDate.getMonth() + 1;
        var day = endDate.getDate();

        var formatedDate = (month + '').padStart(2, '0') + '-' + (day + '').padStart(2, '0');

        if (excludedDates.includes(formatedDate)) {
          continue;
        }
      }

      count++;
    }
  }

  return moment(date).format(returnFormat);
}

function ConvertToDate(date, parseFormat, returnFormat) {
  if (Fn.IsNullOrEmpty(returnFormat)) {
    returnFormat = 'YYYY-MM-DD';
  }
  return moment(date, _CleanMomentFormat(parseFormat)).format(_CleanMomentFormat(returnFormat));
}

function _CurrentLocaleDateFormat() {
  return moment.localeData(getLocale()).longDateFormat('L');
}

function _TryGuessDateFormat(date) {
  if (date != null && date.length > 0) {
    if (date.includes('-') && date.includes('T') && date.includes(':')) {
      return 'YYYY-MM-DDTHH:mm';
    }

    if (date.includes('/')) {
      const { es, en } = Regexp.dateFormats('/');

      // .. check if current date locale is Spanish
      if (es.dt.regexp.test(date)) return es.dt.format;
      if (es.dt2.regexp.test(date)) return es.dt2.format;
      if (es.dt3.regexp.test(date)) return es.dt3.format;

      // .. check if current date locale is English
      if (en.dt.regexp.test(date)) return en.dt.format;
      if (en.dt2.regexp.test(date)) return en.dt2.format;
      if (en.dt3.regexp.test(date)) return en.dt3.format;
    }

    if (date.includes('-')) {
      const arr = date.split('-');
      if (
        arr.length == 3 &&
        arr[0].length == 4 &&
        (arr[1].length == 1 || arr[1].length == 2) &&
        (arr[2].length == 1 || arr[2].length == 2)
      ) {
        return 'YYYY-MM-DD';
      }
    }
    if (date.includes(':')) {
      const arr = date.split(':');
      if (
        (arr.length == 3 || arr.length == 2) &&
        (arr[0].length == 1 || arr[0].length == 2) &&
        (arr[1].length == 1 || arr[1].length == 2)
      ) {
        return 'HH:mm';
      }
    }
  }
  return _CurrentLocaleDateFormat();
}

function _ParseDate(value, parseFormat) {
  if (parseFormat == null || parseFormat.length === 0) {
    parseFormat = _TryGuessDateFormat(value);
  }
  const newDate = moment(value, _PrepareMomentFormat(parseFormat));
  return newDate.toDate();
}

function _PrepareMomentFormat(format) {
  if (format == null || format.length === 0) return '';
  return format.replace('yyyy', 'YYYY').replace('dd', 'DD');
}

function _CleanMomentFormat(format) {
  if (Fn.IsNullOrEmpty(format)) {
    return;
  }
  return format
    .replace('MMMM', 'MXMXMXMX')
    .replace('MMM', 'MLMLML')

    .replace('yyyy', 'YYYY')
    .replace('dd', 'DD');
}

export default {
  AddDays,
  AddMonths,
  AddYears,
  Now,
  DatePart,
  ToDate,
  AddBusinessDays,
  DateDiff,
  ConvertToDate,
  _TryGuessDateFormat,
  _CleanMomentFormat,
};
